import { TEXTFIELD_MASK } from '_components/textfield'
import { validationDate, validationCPF, parseNumber, validationCNPJ } from '_utils/helpers'
import { BANK_ACCOUNT_TYPE_OPTIONS, KEY_PIX_OPTIONS } from '_utils/constants'

export const AGENCY_FIELDS = {
  ADDRESS: 'address',
  ATTACHMENTS: 'agencyAttachments',
  STREET_NUMBER: 'streetNumber',
  BANK: 'bank',
  BANK_ACCOUNT: 'bankAccount',
  BANK_ACCOUNT_CPF_CNPJ: 'bankAccountCpfCnpj',
  BANK_ACCOUNT_OWNER_NAME: 'bankAccountOwnerName',
  BANK_ACCOUNT_TYPE: 'bankAccountType',
  BANK_ACCOUNT_NUMBER: 'bankAccountNumber',
  BANK_ACCOUNT_WITH_DIGIT: 'bankAccountNumber',
  BANK_AGENCY_NUMBER: 'bankAgencyNumber',
  BYPASS: 'bypass',
  MIN_BYPASS_VALUE: 'minBypassValue',
  MAX_BYPASS_VALUE: 'maxBypassValue',
  REQUIRE_ATTACHMENTS_ON_SERVICEORDER_OPENING: 'requireAttachmentsOnServiceorderOpening',
  ENABLE_MARKETING_OPT_IN: 'enableMarketingOptIn',
  CEP: 'cep',
  CITY: 'city',
  EXTRA_ADDRESS_INFO: 'extraAddressInfo',
  CPF_CNPJ: 'cpfCnpj',
  AGENCY_CREATION_DATE: 'agencyCreationDate',
  CONTACT_EMAIL: 'contactEmail',
  NEARBY_LANDMARK: 'nearbyLandmark',
  NEIGHBORHOOD: 'neighborhood',
  PERSON_RESPONSIBLE_NAME: 'personResponsibleName',
  PHONE_NUMBER: 'phoneNumber',
  PIX_KEY: 'pixKey',
  PIX_KEY_CHOICE: 'pixKeyChoice',
  PROFILE_PICTURE: 'profilePicture',
  NAME: 'name',
  IS_ACTIVE: 'isActive',
  STATE: 'state',
  CONTRACT_SOCIAL_STATUTE: 'contractSocialStatute',
  BRAND_LOGO_PNG_URL: 'brandLogoPngUrl',
  FAVICON_URL: 'faviconUrl',
  REGISTRATION_STATUS: 'registrationStatus',
  MAIN_SERVICE_NAMES: 'mainServiceNames',
  STAFF_USERS: 'staffUsers',
  RESPONSIBLE_REFERA: 'responsibleRefera',
  RESPONSIBLE_REFERA_SECTORS: 'responsibleReferaSectors',
  SUPPORT_PHONE: 'supportPhone',
  SUPPORT_WHATSAPP: 'supportWhatsapp',
  SUPPORT_PROVIDER: 'supportProvider',
  SUPPORT_EMAIL: 'supportEmail',
  CONTRACT_TYPE: 'contractType',
  NF_REQUIRED: 'nfRequired',
  FORM_LINK: 'formLink',
  PRIVACY_POLICY_LINK: 'privacyPolicyLink',
  ACCEPTS_CALLS_WITHOUT_VISIT: 'acceptsCallsWithoutVisit',
  REGIME: 'regime',
  CHANNEL_TYPE: 'channelType',
  ALLOWS_SIMPLIFIED_BUDGET: 'allowsSimplifiedBudget',
  ALLOW_EXTERNAL_SERVICE_ORDER_OPENING: 'allowExternalServiceOrderOpening',
}

export const MESSAGE_CHANNEL_STATUS = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  DISCONNECTED_BY_USER: 'disconnected_by_user',
  WAITING_QR: 'waiting_qr_code',
  WAITING_CONNECTION: 'waiting_connection',
}

export const MESSAGE_CHANNEL_STATUS_LABELS = {
  [MESSAGE_CHANNEL_STATUS.CONNECTED]: 'Conectado',
  [MESSAGE_CHANNEL_STATUS.DISCONNECTED]: 'Desconectado',
  [MESSAGE_CHANNEL_STATUS.DISCONNECTED_BY_USER]: 'Desconectado pelo usuário',
  [MESSAGE_CHANNEL_STATUS.WAITING_QR]: 'Aguardando QR Code',
  [MESSAGE_CHANNEL_STATUS.WAITING_CONNECTION]: 'Aguardando conexão',
}

export const INITIAL_STATE = Object.values(AGENCY_FIELDS).reduce((acc, value) => {
  if (value === AGENCY_FIELDS.ALLOW_EXTERNAL_SERVICE_ORDER_OPENING) {
    return {
      ...acc,
      [value]: true,
    }
  }
  if (
    value === AGENCY_FIELDS.REQUIRE_ATTACHMENTS_ON_SERVICEORDER_OPENING ||
    value === AGENCY_FIELDS.ENABLE_MARKETING_OPT_IN ||
    value === AGENCY_FIELDS.ALLOWS_SIMPLIFIED_BUDGET
  ) {
    return {
      ...acc,
      [value]: false,
    }
  }
  if (value === AGENCY_FIELDS.BYPASS || value === AGENCY_FIELDS.NF_REQUIRED) {
    return {
      ...acc,
      [value]: true,
    }
  }

  if (value === AGENCY_FIELDS.MIN_BYPASS_VALUE) {
    return {
      ...acc,
      [value]: 50,
    }
  }

  if (value === AGENCY_FIELDS.MAX_BYPASS_VALUE) {
    return {
      ...acc,
      [value]: 750,
    }
  }

  return {
    ...acc,
    [value]: '',
  }
}, {})

const SITUATION = [
  { label: 'Ativa', value: 'true' },
  { label: 'Desativada', value: 'false' },
]

const REGIME_OPTIONS = [
  { label: 'Simples Nacional', value: 'simples_nacional' },
  { label: 'Lucro Real', value: 'lucro_real' },
  { label: 'Lucro Presumido', value: 'lucro_presumido' },
  { label: 'MEI', value: 'mei' },
]

export const PROFILE_SECTION_FIELDS = [
  {
    name: AGENCY_FIELDS.NAME,
    label: 'Nome da intermediária',
    placeholder: 'Digite o nome',
  },
  {
    name: AGENCY_FIELDS.PERSON_RESPONSIBLE_NAME,
    label: 'Nome do responsável',
    placeholder: 'Digite o nome',
  },
  { name: AGENCY_FIELDS.CONTACT_EMAIL, label: 'Email', placeholder: 'email@email.com' },
]
/**
 * Structure: 3 columns per row
 *
 * Fields' props:
 * @prop {string} name field ID
 * @prop {string} label field label
 * @prop {string} placeholder default text
 * @prop {boolean} isCpfCnpj if the field is a CPF/CNPJ
 * @prop {string} mask mask for formatting input like date, phone etc
 * @prop {Array} options for **select** fields
 * @prop {boolean} disabled not editable field
 * @prop {string} help help info button with popup text on hover
 */
export const AGENCY_VIEW_VALUES = [
  {
    title: 'Dados da empresa',
    id: 'dados-da-empresa-1',
    fields: [
      // ROW 1
      {
        name: AGENCY_FIELDS.CPF_CNPJ,
        label: 'CPF/CNPJ',
        placeholder: '00.000.000/0001-00',
        isCpfCnpj: true,
      },
      {
        name: AGENCY_FIELDS.REGIME,
        label: 'Regime da empresa',
        options: REGIME_OPTIONS,
      },
      {
        name: AGENCY_FIELDS.AGENCY_CREATION_DATE,
        label: 'Data de abertura',
        mask: TEXTFIELD_MASK.DATE,
        placeholder: 'DD/MM/AAAA',
      },
      // ROW 2
      {
        name: AGENCY_FIELDS.CONTRACT_SOCIAL_STATUTE,
        label: 'Contrato / Estatuto social',
        placeholder: '0000000000000',
      },
      {
        name: AGENCY_FIELDS.PRIVACY_POLICY_LINK,
        label: 'Link da política de privacidade',
      },
      {
        name: AGENCY_FIELDS.FORM_LINK,
        label: 'Link formulário de abertura',
        disabled: true,
      },
      {
        name: AGENCY_FIELDS.ALLOW_EXTERNAL_SERVICE_ORDER_OPENING,
        label: 'Permitir abertura externa de Chamado',
        type: 'boolean',
      },

      // ROW 3
      {
        name: AGENCY_FIELDS.PHONE_NUMBER,
        label: 'Telefone',
        placeholder: '(XX) 0 0000-0000',
      },
      {
        name: AGENCY_FIELDS.SUPPORT_PHONE,
        label: 'Suporte por telefone',
        mask: TEXTFIELD_MASK.PHONE_NUMBER,
        placeholder: '(XX) 0 0000-0000',
      },
      {
        name: AGENCY_FIELDS.SUPPORT_WHATSAPP,
        label: 'Suporte por whats',
        placeholder: '(XX) 0 0000-0000',
        help: 'Informe o número de whatsapp que deseja disponibilizar aos **clientes** para dar o suporte a eles por este canal.',
      },

      // ROW 4
      {
        name: AGENCY_FIELDS.SUPPORT_EMAIL,
        label: 'Suporte por email',
        placeholder: 'suporte@exemplo.com',
      },
      {
        name: AGENCY_FIELDS.SUPPORT_PROVIDER,
        label: 'Suporte ao prestador',
        placeholder: '(XX) 0 0000-0000',
        help: 'Informe o número de whatsapp que deseja disponibilizar aos **prestadores** para dar o suporte a eles por este canal. Caso esse campo não seja informado, disponibilizaremos a informação do campo ~Email~ da empresa.',
      },
      {
        name: AGENCY_FIELDS.IS_ACTIVE,
        label: 'Situação',
        options: SITUATION,
      },
    ],
  },
  {
    title: 'Endereço da empresa',
    id: 'endereco-da-empresa-1',
    fields: [
      { name: AGENCY_FIELDS.ADDRESS, label: 'Endereço', placeholder: 'Digite o endereço' },
      { name: AGENCY_FIELDS.STREET_NUMBER, label: 'Número', placeholder: '0000' },
      {
        name: AGENCY_FIELDS.CEP,
        label: 'CEP',
        mask: TEXTFIELD_MASK.CEP,
        placeholder: 'Digite o CEP',
      },
      {
        name: AGENCY_FIELDS.EXTRA_ADDRESS_INFO,
        label: 'Complemento',
        placeholder: 'Digite o complemento',
      },
      { name: AGENCY_FIELDS.NEIGHBORHOOD, label: 'Bairro', placeholder: 'Digite o Bairro' },
      { name: AGENCY_FIELDS.CITY, label: 'Cidade', placeholder: 'Digite a Cidade' },
      {
        name: AGENCY_FIELDS.NEARBY_LANDMARK,
        label: 'Ponto de referência',
        placeholder: 'Ponto de Referência',
      },
      { name: AGENCY_FIELDS.STATE, label: 'Estado', placeholder: 'Estado' },
    ],
  },
  {
    title: 'Dados bancários',
    id: 'dados-bancarios-1',
    fields: [
      {
        name: AGENCY_FIELDS.BANK_ACCOUNT_OWNER_NAME,
        label: 'Nome completo do titular da conta',
        placeholder: 'Digite o nome completo aqui',
      },
      {
        name: AGENCY_FIELDS.BANK_ACCOUNT_TYPE,
        label: 'Operação da conta',
        placeholder: 'Selecione o tipo de conta',
        options: BANK_ACCOUNT_TYPE_OPTIONS,
      },
      {
        name: AGENCY_FIELDS.BANK,
        label: 'Banco',
        placeholder: 'Selecione o banco',
        options: [],
      },
      {
        name: AGENCY_FIELDS.BANK_ACCOUNT_CPF_CNPJ,
        label: 'CPF/CNPJ',
        isCpfCnpj: true,
        placeholder: '000.000.000-00',
      },
      {
        name: AGENCY_FIELDS.BANK_AGENCY_NUMBER,
        label: 'Número da agência',
        placeholder: '0000',
        mask: TEXTFIELD_MASK.AGENCY,
      },
      {
        name: AGENCY_FIELDS.BANK_ACCOUNT_WITH_DIGIT,
        label: 'Número da conta',
        placeholder: '0000000000-0',
        mask: TEXTFIELD_MASK.BANK_WITH_DIGIT,
      },
      {
        name: AGENCY_FIELDS.PIX_KEY_CHOICE,
        label: 'Chave PIX',
        placeholder: 'Selecione a chave pix',
        options: KEY_PIX_OPTIONS,
      },
      { name: AGENCY_FIELDS.PIX_KEY, label: 'Chave', placeholder: 'Digite a chave pix' },
    ],
  },
]

const REQUIRED_FIELDS = [
  AGENCY_FIELDS.PERSON_RESPONSIBLE_NAME,
  AGENCY_FIELDS.NAME,
  AGENCY_FIELDS.CONTACT_EMAIL,
  AGENCY_FIELDS.CPF_CNPJ,
]

export const validate = values => {
  const errors = {}

  REQUIRED_FIELDS.map(value => {
    if (!values[value]) {
      errors[value] = 'Campo obrigatório'
    }
    return null
  })

  if (
    values[AGENCY_FIELDS.AGENCY_CREATION_DATE] &&
    !validationDate(values[AGENCY_FIELDS.AGENCY_CREATION_DATE])
  ) {
    errors[AGENCY_FIELDS.AGENCY_CREATION_DATE] = 'Data inválida'
  }

  if (values[AGENCY_FIELDS.BANK_ACCOUNT_CPF_CNPJ]?.length) {
    const cpfCnpj = parseNumber(values[AGENCY_FIELDS.BANK_ACCOUNT_CPF_CNPJ])

    if (cpfCnpj.length !== 11 && cpfCnpj.length !== 14) {
      errors[AGENCY_FIELDS.BANK_ACCOUNT_CPF_CNPJ] = 'Valor não válido'
    }
    if (cpfCnpj.length === 11 && !validationCPF(cpfCnpj)) {
      errors[AGENCY_FIELDS.BANK_ACCOUNT_CPF_CNPJ] = 'CPF inválido'
    }
    if (cpfCnpj.length === 14 && !validationCNPJ(cpfCnpj)) {
      errors[AGENCY_FIELDS.BANK_ACCOUNT_CPF_CNPJ] = 'CNPJ inválido'
    }
    if (cpfCnpj.length > 14) {
      errors[AGENCY_FIELDS.BANK_ACCOUNT_CPF_CNPJ] = 'Valor não válido'
    }
  }

  if (values[AGENCY_FIELDS.CNPJ]?.length) {
    const cpfCnpj = parseNumber(values[AGENCY_FIELDS.CPF_CNPJ])

    if (cpfCnpj.length !== 11 && cpfCnpj.length !== 14) {
      errors[AGENCY_FIELDS.CNPJ] = 'Valor não válido'
    }
    if (cpfCnpj.length === 11 && !validationCPF(cpfCnpj)) {
      errors[AGENCY_FIELDS.CNPJ] = 'CPF inválido'
    }
    if (cpfCnpj.length === 14 && !validationCNPJ(cpfCnpj)) {
      errors[AGENCY_FIELDS.CNPJ] = 'CNPJ inválido'
    }
  }

  if (!values[AGENCY_FIELDS.PERSON_RESPONSIBLE_NAME]?.length) {
    errors[AGENCY_FIELDS.PERSON_RESPONSIBLE_NAME] = 'Insira um nome de responsável'
  }
  if (!values[AGENCY_FIELDS.NAME]?.length) {
    errors[AGENCY_FIELDS.NAME] = 'Insira um nome'
  }

  if (!values[AGENCY_FIELDS.CONTACT_EMAIL]?.length) {
    errors[AGENCY_FIELDS.CONTACT_EMAIL] = 'Insira um email'
  }
  if (!values[AGENCY_FIELDS.CPF_CNPJ]?.length) {
    errors[AGENCY_FIELDS.CPF_CNPJ] = 'Insira um CPF/CNPJ'
  }

  if (values[AGENCY_FIELDS.BYPASS]) {
    const maxBypassValue = Number(values[AGENCY_FIELDS.MAX_BYPASS_VALUE])
    const minBypassValue = Number(values[AGENCY_FIELDS.MIN_BYPASS_VALUE])

    if (!maxBypassValue && maxBypassValue !== 0) {
      errors[AGENCY_FIELDS.MAX_BYPASS_VALUE] = 'Campo obrigatório'
    }

    if (maxBypassValue > 999999.99) {
      errors[AGENCY_FIELDS.MAX_BYPASS_VALUE] = 'Valor deve ser menor que R$ 999999,99'
    }

    if (minBypassValue > 999999.99) {
      errors[AGENCY_FIELDS.MIN_BYPASS_VALUE] = 'Valor deve ser menor que R$ 999999,99'
    }

    if (!minBypassValue && minBypassValue !== 0) {
      errors[AGENCY_FIELDS.MIN_BYPASS_VALUE] = 'Campo obrigatório'
    }
  }

  return errors
}

export const ColumnsBuilding = () => [
  {
    field: 'buildingTypeDesc',
    headerName: 'Tipo',
    flex: 1,
    editable: false,
  },
  {
    field: 'name',
    headerName: 'Nome',
    flex: 1,
    editable: false,
  },
  {
    field: 'responsible',
    headerName: 'Responsável',
    flex: 1,
    editable: false,
  },
  {
    field: 'phone',
    headerName: 'Telefone',
    flex: 1,
    editable: false,
  },
]

export const ColumnsWebhook = () => [
  {
    field: 'whInformation',
    headerName: 'Informação',
    flex: 1,
    editable: false,
    valueGetter: params => params?.row?.trigger?.whInformation ?? '',
  },
  {
    field: 'whTrigger',
    headerName: 'Gatilho',
    flex: 1,
    editable: false,
    valueGetter: params => params?.row?.trigger?.whTrigger ?? '',
  },
  {
    field: 'apiCall',
    headerName: 'API para chamar',
    flex: 1,
    editable: false,
  },
  {
    field: 'returnSuccess',
    headerName: 'Retornos de sucesso',
    flex: 1,
    editable: false,
  },
  {
    field: 'returnError',
    headerName: 'Retornos de erro',
    flex: 1,
    editable: false,
  },
  {
    field: 'viewRequests',
    headerName: 'Ver requisições',
    flex: 1,
    editable: false,
    sortable: false,
    renderCell: () => 'Ver requisições',
  },
]

export const ColumnsWebhookRequests = () => [
  {
    field: 'created_at',
    headerName: 'Data de criação',
    flex: 1,
    editable: false,
    valueFormatter: params => {
      if (!params.value) return '-'
      return new Date(params.value).toLocaleString('pt-BR')
    },
  },
  {
    field: 'resend_at',
    headerName: 'Data de envio',
    flex: 1,
    editable: false,
    valueFormatter: params => {
      if (!params.value) return '-'
      return new Date(params.value).toLocaleString('pt-BR')
    },
  },
  {
    field: 'api_url',
    headerName: 'API',
    flex: 1,
    editable: false,
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    editable: false,
  },
  {
    field: 'api_response',
    headerName: 'Resultado',
    flex: 1,
    editable: false,
  },
  {
    field: 'json_sent',
    headerName: 'Json enviado',
    flex: 1,
    editable: false,
    sortable: false,
    renderCell: () => 'Json enviado',
  },
]
